import type { Feature } from "../matter/copy"

/** The copy of the "What is a workspace?" page, as data. WorkspaceAbout.tsx renders it; about/ holds the walkthrough. */
export const ABOUT_HERO = {
  overline: "Workspaces",
  title: "Everything for one case, in one place.",
  sub: "A workspace is a folder for one case: a notice, a dispute, a taxpayer's file. Research, notice analyses, appeal checks and OIO or OIA drafts live together, and the files you add are shared by all of them.",
  primary: { label: "Create a workspace", to: "/workspaces?new=1" },
  secondary: { label: "Open your workspaces", to: "/workspaces" },
}

export const ABOUT_WORKSPACE_NAME = "Acme Traders"

export const ABOUT_FILES = [
  { name: "Show cause notice", kind: "PDF" },
  { name: "Reply to notice", kind: "DOC" },
  { name: "Annexure A", kind: "PDF" },
] as const

/** The five features in the order they light up; `what` is the one-line meaning shown beside each tag. */
export const ABOUT_FEATURES: { feature: Feature; what: string }[] = [
  { feature: "research", what: "A question about the law" },
  { feature: "notice", what: "A reading of a notice" },
  { feature: "appeal", what: "Whether an appeal is worth it" },
  { feature: "oio", what: "A reply to a notice, drafted" },
  { feature: "oia", what: "An appeal against an order, drafted" },
]

export const ABOUT_SESSIONS: { title: string; feature: Feature; date: string }[] = [
  { title: "Is ITC allowed on the Acme invoices?", feature: "research", date: "14 Oct" },
  { title: "Reading of SCN 04/2025-26", feature: "notice", date: "12 Oct" },
  { title: "Reply to SCN 04/2025-26", feature: "oio", date: "10 Oct" },
]

export interface AboutStep {
  n: string
  /** The short name on the step rail. */
  label: string
  title: string
  text: string
  /** Read aloud in place of the aria-hidden scene. */
  alt: string
}

export const ABOUT_STEPS: AboutStep[] = [
  {
    n: "01",
    label: "Create",
    title: "Create a workspace for the case",
    text: "Name it after the case and, if you like, add the notice number. It takes a few seconds, and you can also create one from “Add to workspace” on any page.",
    alt: "A new workspace named Acme Traders is created.",
  },
  {
    n: "02",
    label: "Files",
    title: "Add the files once",
    text: "Drop in the notice, the reply and the annexures. They are stored on the workspace, not on any one session.",
    alt: "Three files, the notice, the reply and Annexure A, slide into the workspace.",
  },
  {
    n: "03",
    label: "Features",
    title: "Use any feature on those files",
    text: "Research, notice analysis, appeal checks and order drafts all draw on the same files. Nothing is added again.",
    alt: "Research, Notice analysis, Appeal or not, Order and Order draft OIA each draw on the same files.",
  },
  {
    n: "04",
    label: "Sessions",
    title: "See everything together",
    text: "Every session in the workspace is listed with its feature tag, and you can filter by feature.",
    alt: "A list of the sessions in the workspace, each with its feature tag, and a filter by feature.",
  },
]

export const ABOUT_TOUR = {
  eyebrow: "How it works",
  title: "Four steps, then it stays organised.",
  label: "How a workspace works",
}

export const ABOUT_HELPS = {
  eyebrow: "Why it helps",
  title: "Less repeating yourself.",
  points: [
    { title: "Never add the same file twice", text: "A file added to the workspace is there for every session in it." },
    { title: "Nira picks what it needs", text: "It reads the file summaries and chooses the ones it needs, or asks you for what is missing." },
    { title: "One place to come back to", text: "Open the workspace and everything on the case is there, however many sessions it took." },
  ],
}

export const ABOUT_FAQ = {
  eyebrow: "Questions",
  title: "Good to know.",
  items: [
    { q: "Can a session be in two workspaces?", a: "No. A session belongs to one workspace at a time, but you can move it to another." },
    { q: "Can I move a session later?", a: "Yes. Open the session, choose “Add to workspace”, and pick another workspace, or remove it from this one." },
    { q: "Who can see a workspace?", a: "Only you. A workspace and its files belong to your account." },
    { q: "What can I put in a workspace?", a: "The files for the case, such as the notice, your reply, annexures and invoices, and the sessions you run on them." },
  ],
}

export const ABOUT_CLOSE = {
  title: "Start with one case.",
  sub: "Create the workspace, add the notice, and take it from there.",
}
